import {
  type CreateImageJob,
  type ImageJob,
  imageJobListSchema,
  imageJobSchema,
} from '@acasa/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { API_PREFIX, apiFetch } from '@/lib/api'

/**
 * Every job, newest first.
 *
 * Deliberately unfiltered: the history shows all of them and the per-estate
 * queue is a filter over the same array, so one query and one poll serve both.
 * The API still takes `?estate=`, which is what a paginated history would use.
 *
 * Gemini reports no progress and hands back one image at the end, so there is
 * nothing to stream: the list is polled while anything is unfinished and left
 * alone once everything has settled. Polling stops on its own rather than
 * running forever in a background tab.
 */
export function useImageJobs() {
  const key = ['image-jobs'] as const

  const query = useQuery({
    queryKey: key,
    queryFn: () => apiFetch('/image-jobs', imageJobListSchema),
    refetchInterval: (query) => {
      const jobs = query.state.data?.jobs ?? []
      const busy = jobs.some((job) => job.status === 'queued' || job.status === 'running')
      return busy ? 2_000 : false
    },
  })

  const queryClient = useQueryClient()

  const submit = useMutation({
    mutationFn: (input: CreateImageJob) =>
      apiFetch('/image-jobs', imageJobSchema, { method: 'POST', body: input }),
    onSuccess: (job) => {
      // Show the queued job at once instead of waiting a poll interval for it.
      queryClient.setQueryData(
        key,
        (previous: { jobs: ImageJob[]; configured: boolean } | undefined) =>
          previous ? { ...previous, jobs: [job, ...previous.jobs] } : previous,
      )
      void queryClient.invalidateQueries({ queryKey: key })
    },
  })

  // Plain fetch, like the other deletes in this app: 204 has no body, and
  // apiFetch always wants a schema to parse.
  const remove = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`${API_PREFIX}/image-jobs/${id}`, { method: 'DELETE' })
      if (!response.ok) throw new Error('Auftrag konnte nicht entfernt werden')
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
  })

  return {
    jobs: query.data?.jobs ?? [],
    configured: query.data?.configured ?? true,
    isPending: query.isPending,
    submit,
    remove,
  }
}
